import { useEffect, useState } from 'react';
import { Tablet, Loader2, Check } from 'lucide-react';
import { api } from '../lib/spizirnaUi';

const VZHLEDY = [
    { kod: 'tmavy', nazev: 'Tmavý' },
    { kod: 'svetly', nazev: 'Světlý' },
    { kod: 'cas', nazev: 'Podle denní doby' },
];

const JAZYKY = [
    { kod: 'cs', nazev: 'Čeština' },
    { kod: 'en', nazev: 'Angličtina' },
    { kod: 'de', nazev: 'Němčina' },
    { kod: 'uk', nazev: 'Ukrajinština' },
    { kod: 'pl', nazev: 'Polština' },
];

/**
 * Nastavení kiosku s AI Sárou — platí pro všechny kiosky
 * pobočky. Co se na kiosku prodává, se nastavuje jinde: přepínačem
 * „Kiosek" u kategorie v receptáři.
 *
 * Text „O bistru" je jediné, co Sára o provozu ví mimo nabídku — otevírací
 * doba, adresa, wifi, novinky. Co v něm není, Sára nevymýšlí a pošle hosta
 * za obsluhou.
 */
export default function KiosekNastaveni({ isDark, barva, muted, border, fieldBg, kartaBg }) {
    const [n, setN] = useState(null);
    const [pin, setPin] = useState('');
    const [ulozeno, setUlozeno] = useState(false);
    const [chyba, setChyba] = useState('');

    useEffect(() => {
        api('/api/kiosek-nastaveni').then(setN).catch(() => setChyba('Nastavení kiosku se nepodařilo načíst.'));
    }, []);

    if (!n) return chyba
        ? <p className="text-xs" style={{ color: '#EF4444' }}>{chyba}</p>
        : <Loader2 size={16} className="animate-spin" style={{ color: muted }} />;

    const uloz = async (zmena) => {
        setChyba('');
        setUlozeno(false);
        try {
            setN(await api('/api/kiosek-nastaveni', 'PUT', zmena));
            setUlozeno(true);
            setTimeout(() => setUlozeno(false), 1800);
        } catch (e) {
            setChyba(e?.message || e?.error || 'Nepodařilo se uložit.');
        }
    };

    const prepnoutJazyk = (kod) => {
        if (kod === 'cs') return;
        const jazyky = n.jazyky.includes(kod) ? n.jazyky.filter(j => j !== kod) : [...n.jazyky, kod];
        uloz({ jazyky });
    };

    const prepinac = (on) => (
        <span style={{ width: 30, height: 18, borderRadius: 9, position: 'relative', flexShrink: 0, background: on ? barva : (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)') }}>
            <span style={{ position: 'absolute', top: 2, left: on ? 14 : 2, width: 14, height: 14, borderRadius: 7, background: '#fff' }} />
        </span>
    );

    return (
        <div className="rounded-xl px-4 py-4 space-y-4" style={{ background: kartaBg, border: `1px solid ${border}` }}>
            <div className="flex items-center gap-2">
                <Tablet size={16} style={{ color: barva }} />
                <h4 className="text-sm font-semibold flex-1" style={{ color: 'var(--text-primary)' }}>Kiosek s AI Sárou</h4>
                {ulozeno && <span className="flex items-center gap-1 text-[11px]" style={{ color: '#10b981' }}><Check size={12} /> uloženo</span>}
            </div>
            <p className="text-xs" style={{ color: muted }}>
                Platí pro všechny kiosky. Co kiosek nabízí, zapneš v receptáři přepínačem „Kiosek" u kategorie.
                Objednávka z kiosku přijde do pokladny jako nezaplacený účet — do kuchyně jde až po zaplacení.
            </p>

            <div>
                <label className="block text-[11px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>O bistru — co Sára ví</label>
                <textarea defaultValue={n.o_bistru} rows={5}
                    onBlur={e => e.target.value !== n.o_bistru && uloz({ o_bistru: e.target.value })}
                    placeholder={'Otevřeno Po–Pá 10–21, So–Ne 11–20.\nWifi: FofrLama, heslo …\nTento týden novinka: …'}
                    className="w-full rounded-lg px-3 py-2 text-sm"
                    style={{ background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)', resize: 'vertical' }} />
                <p className="text-[11px] mt-1" style={{ color: muted }}>Co tu není, Sára nevymýšlí — pošle hosta za obsluhou.</p>
            </div>

            <div>
                <div className="text-[11px] font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>Jazyky</div>
                <div className="flex flex-wrap gap-1.5">
                    {JAZYKY.map(j => {
                        const on = n.jazyky.includes(j.kod);
                        return (
                            <button key={j.kod} type="button" onClick={() => prepnoutJazyk(j.kod)}
                                className="rounded-lg px-2.5 py-1.5 text-xs font-medium"
                                title={j.kod === 'cs' ? 'Čeština zůstává vždy' : undefined}
                                style={{ background: on ? `${barva}22` : fieldBg, border: `1px solid ${on ? barva : border}`, color: on ? 'var(--text-primary)' : muted, cursor: j.kod === 'cs' ? 'default' : 'pointer' }}>
                                {j.nazev}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div>
                <div className="text-[11px] font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>Vzhled kiosku</div>
                <div className="flex flex-wrap gap-1.5">
                    {VZHLEDY.map(v => {
                        const on = (n.vzhled ?? 'tmavy') === v.kod;
                        return (
                            <button key={v.kod} type="button" onClick={() => uloz({ vzhled: v.kod })}
                                className="rounded-lg px-2.5 py-1.5 text-xs font-medium"
                                style={{ background: on ? `${barva}22` : fieldBg, border: `1px solid ${on ? barva : border}`, color: on ? 'var(--text-primary)' : muted, cursor: 'pointer' }}>
                                {v.nazev}
                            </button>
                        );
                    })}
                </div>
                {n.vzhled === 'cas' && (
                    <div className="flex items-center gap-2 mt-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                        Světlý od
                        <input type="time" defaultValue={n.vzhled_od} onBlur={e => e.target.value && e.target.value !== n.vzhled_od && uloz({ vzhled_od: e.target.value })}
                            className="rounded-lg px-2 text-xs" style={{ height: 30, background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)' }} />
                        do
                        <input type="time" defaultValue={n.vzhled_do} onBlur={e => e.target.value && e.target.value !== n.vzhled_do && uloz({ vzhled_do: e.target.value })}
                            className="rounded-lg px-2 text-xs" style={{ height: 30, background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)' }} />
                        , jinak tmavý
                    </div>
                )}
                <p className="text-[11px] mt-1" style={{ color: muted }}>Tmavý odpovídá brand booku. Změna se na kiosku projeví do minuty, bez restartu.</p>
            </div>

            <button type="button" onClick={() => uloz({ titulky: !n.titulky })}
                className="flex items-center gap-2 text-xs" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', padding: 0 }}>
                {prepinac(n.titulky)}
                Titulky pod Sárou (co právě řekla)
            </button>

            <div className="flex flex-wrap items-end gap-2">
                <div>
                    <label className="block text-[11px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
                        Servisní PIN {n.ma_servisni_pin ? '(nastavený)' : '(nenastavený)'}
                    </label>
                    <input value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))} inputMode="numeric"
                        placeholder="4–8 číslic" className="rounded-lg px-3 text-sm font-mono"
                        style={{ height: 36, width: 140, background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)' }} />
                </div>
                <button type="button" disabled={pin.length < 4} onClick={() => { uloz({ servisni_pin: pin }); setPin(''); }}
                    className="rounded-lg text-xs font-semibold px-3"
                    style={{ height: 36, background: pin.length >= 4 ? barva : 'transparent', color: pin.length >= 4 ? '#fff' : muted, border: `1px solid ${pin.length >= 4 ? barva : border}`, cursor: pin.length >= 4 ? 'pointer' : 'not-allowed' }}>
                    Nastavit
                </button>
                <p className="basis-full text-[11px]" style={{ color: muted }}>
                    Na kiosku pětkrát rychle ťukni na logo vlevo nahoře a zadej PIN — otevře se servisní menu (ukončit kiosek, obnovit).
                </p>
            </div>

            {chyba && <p className="text-xs" style={{ color: '#EF4444' }}>{chyba}</p>}
        </div>
    );
}
